import React from 'react';

// Drop-in replacement for next/dynamic, aliased at bundle time.
//
// next/dynamic is used two ways across the card components:
//   dynamic(() => import('react-stars'), { ssr: false })   // no Suspense
//   dynamic(() => import('./VideoCard'), { suspense: true }) // wrapped in Suspense
//
// A React.lazy-based shim would crash the first form (lazy requires a Suspense
// boundary), so this implements a tiny loadable: render the loading element
// (or nothing) until the chunk resolves, then swap in the real component.
// esbuild inlines the dynamic import() into the IIFE bundle, so resolution is
// effectively immediate.

// A component object (memo / forwardRef / lazy) is as renderable as a
// function, so identity - not typeof - decides when unwrapping stops.
function isRenderable(value) {
  return (
    typeof value === 'function' ||
    (value !== null && typeof value === 'object' && value.$$typeof !== undefined)
  );
}

// Unwrap whatever the loader resolved to down to an actual component. Three
// shapes arrive here depending on the module and the bundle:
//
//   fn                                            already a component
//   { default: fn }                               ES module namespace (the .jsx cards)
//   { default: { __esModule: true, default: fn } } a CommonJS dependency (react-stars)
//                                                 reached through a REAL dynamic import
//
// The third only happens in the code-split ESM build (famewall-widget.js):
// esbuild applies its CommonJS interop when it *inlines* an import() - which
// is what the IIFE bundle (newFrame.js) does - but a split build emits the
// dependency as its own chunk whose default export is the raw module.exports,
// with no interop at the import site. Handing that middle object to
// createElement is React error #130 ("element type is invalid ... but got:
// object"), which takes down the whole embed root, so peel it here.
function resolveComponent(mod) {
  let candidate = mod;

  for (let depth = 0; depth < 5; depth += 1) {
    if (isRenderable(candidate)) {
      return candidate;
    }

    if (!candidate || typeof candidate !== 'object' || !candidate.default) {
      return null;
    }

    candidate = candidate.default;
  }

  return null;
}

export default function dynamic(loader, options = {}) {
  let LoadedComponent = null;
  let loadStarted = false;
  const subscribers = new Set();

  function startLoading() {
    if (loadStarted) {
      return;
    }

    loadStarted = true;

    Promise.resolve()
      .then(() => loader())
      .then((mod) => {
        LoadedComponent = resolveComponent(mod);

        if (!LoadedComponent) {
          // Nothing renderable in the chunk: stay in the loading state rather
          // than throwing React error #130 into the host page.
          return;
        }

        subscribers.forEach((notify) => notify());
      })
      .catch(() => {
        // Leave LoadedComponent null; the embed degrades to the loading state
        // instead of throwing into the host page.
      });
  }

  function DynamicComponent(props) {
    const [, forceRender] = React.useReducer((tick) => tick + 1, 0);

    React.useEffect(() => {
      if (LoadedComponent) {
        return undefined;
      }

      subscribers.add(forceRender);
      startLoading();

      return () => {
        subscribers.delete(forceRender);
      };
    }, []);

    if (LoadedComponent) {
      return React.createElement(LoadedComponent, props);
    }

    if (options && typeof options.loading === 'function') {
      return React.createElement(options.loading, { isLoading: true, pastDelay: true, error: null });
    }

    return null;
  }

  DynamicComponent.displayName = 'DynamicShim';

  return DynamicComponent;
}
